import type {ReactElement} from 'react';
import React from 'react';
import {TextItemBaseBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-item-base-background-option';
import type {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromBackgroundOfTextActiveItem} from '@Hooks/poster-editor/useGetKeyFromBackgroundOfTextActiveItem';
import {useGetKeyFromFillOfBackgroundOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromFillOfBackgroundOfActiveItem';
import type {RGBA} from '@Utils/color.util';

interface ItemBackgroundOptionProps {
  onClickColorFill(): void;
}

export function ItemBackgroundOption({...props}: ItemBackgroundOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const rx = useGetKeyFromBackgroundOfTextActiveItem('rx');
  const ry = useGetKeyFromBackgroundOfTextActiveItem('ry');
  const bgFillColor = useGetKeyFromFillOfBackgroundOfActiveItem('fillColor');
  const bgFillType = useGetKeyFromFillOfBackgroundOfActiveItem('fillType');

  if (!activeItem || (activeItem && !('background' in activeItem)) || rx === undefined || ry === undefined) {
    throw new Error(`Item doesn't have background property`);
  }

  if (!('fill' in activeItem.background) || !bgFillColor || bgFillType === undefined) {
    throw new Error(`Item doesn't have fill in background property`);
  }

  const onChangeFillType = (newType: FillTypes): void => {
    void activeItem.updateFromObject({
      background: {
        fill: {
          fillType: newType,
          fillColor: activeItem.background.fill.getColorForNewType(newType),
        },
      },
    });
  };

  const updateOpacity = (fillAlpha: number, undoable = true): void => {
    const fillColor = bgFillColor.slice();
    for (let i = 0; i < fillColor.length; i++) {
      const color = fillColor[i].slice();
      color[3] = fillAlpha;
      fillColor[i] = color as RGBA;
    }

    void activeItem.updateFromObject(
      {
        background: {
          fill: {
            fillColor,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const updateRoundess = (val: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        background: {
          rx: val,
          ry: val,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <TextItemBaseBackgroundOption
      onChangeFillType={onChangeFillType}
      onUpdateOpacity={updateOpacity}
      onUpdateRoundness={updateRoundess}
      background={{
        rx,
        ry,
        fill: {
          fillType: bgFillType,
          fillColor: bgFillColor,
        },
      }}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
    />
  );
}
